import { nextTick } from 'vue';

export const BULLETED_TEXTAREA_PREFIX = '• ';
const BULLET_PREFIX_PATTERN = /^[\t ]*(?:\u2022[\t ]?|[-*][\t ]+)/;

// Parsing stays lossless while the textarea is active. Trimming here would make
// Vue rewrite the value and move the caret when the user types whitespace.
const stripBulletPrefix = (value) => String(value ?? '').replace(BULLET_PREFIX_PATTERN, '');

const getBulletPrefixLength = (line = '') => {
	const match = String(line).match(BULLET_PREFIX_PATTERN);
	return match ? match[0].length : 0;
};

export const splitBulletedText = (value) =>
	String(value || '')
		.split(/\r?\n/)
		.map(stripBulletPrefix);

export const listToBulletedText = (list = []) =>
	(Array.isArray(list) ? list : [])
		.map((item) => `${BULLETED_TEXTAREA_PREFIX}${String(item ?? '')}`)
		.join('\n');

const getBulletedLineRanges = (value) => {
	const rawLines = String(value ?? '').split('\n');
	let lineStart = 0;
	return rawLines.map((rawLine, lineIndex) => {
		const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
		const lineEnd = lineStart + line.length;
		const prefixLength = getBulletPrefixLength(line);
		const content = line.slice(prefixLength);
		const leadingWhitespaceLength = content.match(/^\s*/)?.[0].length || 0;
		const trailingWhitespaceLength = content.match(/\s*$/)?.[0].length || 0;
		const range = {
			lineIndex,
			lineStart,
			lineEnd,
			meaningfulContentStart: lineStart + prefixLength + leadingWhitespaceLength,
			meaningfulContentEnd: lineEnd - trailingWhitespaceLength,
		};
		lineStart += rawLine.length + 1;
		return range;
	});
};

const getFullySelectedBulletedLineIndexes = (value, selectionStart, selectionEnd) => {
	if (selectionStart === selectionEnd) return [];
	const selectedIndexes = [];

	for (const range of getBulletedLineRanges(value)) {
		const hasMeaningfulContent = range.meaningfulContentEnd > range.meaningfulContentStart;
		const coversWholeContent = hasMeaningfulContent
			? selectionStart <= range.meaningfulContentStart && selectionEnd >= range.meaningfulContentEnd
			: selectionStart <= range.lineStart && selectionEnd >= range.lineEnd;
		const overlapsContent =
			hasMeaningfulContent &&
			selectionStart < range.meaningfulContentEnd &&
			selectionEnd > range.meaningfulContentStart;

		if (overlapsContent && !coversWholeContent) return [];
		if (coversWholeContent) selectedIndexes.push(range.lineIndex);
	}

	return selectedIndexes;
};

const getBulletedCaretPosition = (lines, lineIndex, contentOffset = 0) => {
	const safeLines = Array.isArray(lines) && lines.length ? lines : [''];
	const safeIndex = Math.max(0, Math.min(lineIndex, safeLines.length - 1));
	let position = 0;
	for (let index = 0; index < safeIndex; index += 1) {
		position += BULLETED_TEXTAREA_PREFIX.length + String(safeLines[index] || '').length + 1;
	}
	return position + BULLETED_TEXTAREA_PREFIX.length + Math.max(0, contentOffset);
};

const getBulletedLineInfo = (value, caret) => {
	const text = String(value || '');
	const safeCaret = Math.max(0, Math.min(caret || 0, text.length));
	const rawLines = text.split(/\r?\n/);
	let lineStart = 0;

	for (let lineIndex = 0; lineIndex < rawLines.length; lineIndex += 1) {
		const rawLine = rawLines[lineIndex] || '';
		const lineEnd = lineStart + rawLine.length;
		if (safeCaret <= lineEnd || lineIndex === rawLines.length - 1) {
			const lineCaret = Math.max(0, safeCaret - lineStart);
			const prefixLength = getBulletPrefixLength(rawLine);
			const content = stripBulletPrefix(rawLine);
			return {
				lineIndex,
				lineCaret,
				prefixLength,
				content,
				contentCaret: Math.max(0, Math.min(content.length, lineCaret - prefixLength)),
			};
		}
		lineStart = lineEnd + 1;
	}

	return {
		lineIndex: 0,
		lineCaret: 0,
		prefixLength: BULLETED_TEXTAREA_PREFIX.length,
		content: '',
		contentCaret: 0,
	};
};

export const createBulletedTextareaEditor = ({ scheduleResize = null } = {}) => {
	const setTextareaCaret = (textarea, caret) => {
		if (!textarea || typeof textarea.setSelectionRange !== 'function') return;
		void nextTick(() => {
			const safeCaret = Math.max(0, Math.min(caret, String(textarea.value || '').length));
			textarea.focus?.();
			textarea.setSelectionRange(safeCaret, safeCaret);
			if (typeof scheduleResize === 'function') scheduleResize(textarea);
		});
	};

	const commitBulletedTextareaLines = (target, key, lines, textarea, caret) => {
		if (!target || !key) return;
		const nextLines = Array.isArray(lines) ? lines : [];
		target[key] = nextLines;
		if (textarea) {
			textarea.value = listToBulletedText(nextLines);
			setTextareaCaret(textarea, caret);
		}
	};

	const updateBulletedStringList = (target, key, value, textarea = null) => {
		if (!target || !key) return;
		const text = String(value ?? '');
		const lines = text ? splitBulletedText(text) : [];
		target[key] = lines;

		const normalizedText = listToBulletedText(lines);
		if (!textarea || normalizedText === text) return;
		const lineInfo = getBulletedLineInfo(text, textarea.selectionStart);
		const caret = lines.length
			? getBulletedCaretPosition(lines, lineInfo.lineIndex, lineInfo.contentCaret)
			: 0;
		textarea.value = normalizedText;
		setTextareaCaret(textarea, caret);
	};

	const handleBulletedTextareaEnter = (event, target, key) => {
		const textarea = event?.target;
		if (!textarea || event.isComposing) return;
		event.preventDefault();
		if (textarea.selectionStart !== textarea.selectionEnd) {
			const lineInfo = getBulletedLineInfo(textarea.value, textarea.selectionStart);
			const nextValue = `${textarea.value.slice(0, textarea.selectionStart)}\n${textarea.value.slice(textarea.selectionEnd)}`;
			const lines = splitBulletedText(nextValue);
			const nextLineIndex = lineInfo.lineIndex + 1;
			const caret = getBulletedCaretPosition(lines, nextLineIndex, 0);
			commitBulletedTextareaLines(target, key, lines, textarea, caret);
			return;
		}

		const lines = splitBulletedText(textarea.value);
		const lineInfo = getBulletedLineInfo(textarea.value, textarea.selectionStart);
		const currentLine = lines[lineInfo.lineIndex] || '';
		const beforeCaret = currentLine.slice(0, lineInfo.contentCaret);
		const afterCaret = currentLine.slice(lineInfo.contentCaret);
		lines.splice(lineInfo.lineIndex, 1, beforeCaret, afterCaret);

		const nextLineIndex = lineInfo.lineIndex + 1;
		const caret = getBulletedCaretPosition(lines, nextLineIndex, 0);
		commitBulletedTextareaLines(target, key, lines, textarea, caret);
	};

	const removeFullySelectedBulletedLines = (event, target, key) => {
		const textarea = event?.target;
		if (!textarea || textarea.selectionStart === textarea.selectionEnd) return false;
		const selectedIndexes = getFullySelectedBulletedLineIndexes(
			textarea.value,
			textarea.selectionStart,
			textarea.selectionEnd,
		);
		if (!selectedIndexes.length) return false;

		event.preventDefault();
		const selectedIndexSet = new Set(selectedIndexes);
		const lines = splitBulletedText(textarea.value);
		const firstSelectedIndex = selectedIndexes[0];
		const remainingLines = lines.filter((_line, index) => !selectedIndexSet.has(index));
		let caret = 0;
		if (remainingLines.length) {
			const hasNextLine = firstSelectedIndex < remainingLines.length;
			const caretLineIndex = hasNextLine ? firstSelectedIndex : remainingLines.length - 1;
			const contentOffset = hasNextLine ? 0 : String(remainingLines[caretLineIndex] || '').length;
			caret = getBulletedCaretPosition(remainingLines, caretLineIndex, contentOffset);
		}
		commitBulletedTextareaLines(target, key, remainingLines, textarea, caret);
		return true;
	};

	const handleBulletedTextareaBackspace = (event, target, key) => {
		const textarea = event?.target;
		if (!textarea || event.isComposing) return;

		if (textarea.selectionStart !== textarea.selectionEnd) {
			removeFullySelectedBulletedLines(event, target, key);
			return;
		}

		const lines = splitBulletedText(textarea.value);
		const lineInfo = getBulletedLineInfo(textarea.value, textarea.selectionStart);
		const currentLine = lines[lineInfo.lineIndex] || '';
		const caretIsInPrefix = lineInfo.lineCaret <= lineInfo.prefixLength;
		if (!caretIsInPrefix) return;

		event.preventDefault();

		if (!currentLine.trim()) {
			if (lines.length === 1) {
				commitBulletedTextareaLines(target, key, [], textarea, 0);
				return;
			}
			const removingFirstLine = lineInfo.lineIndex === 0;
			const previousLineIndex = Math.max(0, lineInfo.lineIndex - 1);
			lines.splice(lineInfo.lineIndex, 1);
			const contentOffset = removingFirstLine ? 0 : String(lines[previousLineIndex] || '').length;
			const caret = getBulletedCaretPosition(lines, previousLineIndex, contentOffset);
			commitBulletedTextareaLines(target, key, lines, textarea, caret);
			return;
		}

		if (lineInfo.lineIndex === 0) {
			commitBulletedTextareaLines(
				target,
				key,
				lines,
				textarea,
				getBulletedCaretPosition(lines, 0, 0),
			);
			return;
		}

		const previousLineIndex = lineInfo.lineIndex - 1;
		const previousLine = lines[previousLineIndex] || '';
		lines[previousLineIndex] = `${previousLine}${currentLine}`;
		lines.splice(lineInfo.lineIndex, 1);
		const caret = getBulletedCaretPosition(lines, previousLineIndex, previousLine.length);
		commitBulletedTextareaLines(target, key, lines, textarea, caret);
	};

	const handleBulletedTextareaDelete = (event, target, key) => {
		const textarea = event?.target;
		// Vue's `.delete` key modifier also aliases Backspace.
		if (!textarea || event.isComposing || event.key !== 'Delete') return;
		if (textarea.selectionStart !== textarea.selectionEnd) {
			removeFullySelectedBulletedLines(event, target, key);
			return;
		}

		const lines = splitBulletedText(textarea.value);
		const lineInfo = getBulletedLineInfo(textarea.value, textarea.selectionStart);
		if (lineInfo.lineCaret < lineInfo.prefixLength) {
			event.preventDefault();
			setTextareaCaret(textarea, getBulletedCaretPosition(lines, lineInfo.lineIndex, 0));
			return;
		}

		const lineRange = getBulletedLineRanges(textarea.value)[lineInfo.lineIndex];
		if (
			!lineRange ||
			textarea.selectionStart !== lineRange.lineEnd ||
			lineInfo.lineIndex >= lines.length - 1
		) return;

		event.preventDefault();
		const currentLine = lines[lineInfo.lineIndex] || '';
		lines[lineInfo.lineIndex] = `${currentLine}${lines[lineInfo.lineIndex + 1] || ''}`;
		lines.splice(lineInfo.lineIndex + 1, 1);
		const caret = getBulletedCaretPosition(lines, lineInfo.lineIndex, currentLine.length);
		commitBulletedTextareaLines(target, key, lines, textarea, caret);
	};

	return {
		handleBulletedTextareaBackspace,
		handleBulletedTextareaDelete,
		handleBulletedTextareaEnter,
		updateBulletedStringList,
	};
};
